import { continuePath, PWA_PROTOCOL_SCHEME } from "./config";
import { isStandalone } from "./isStandalone";
import {
	armContinueOnce,
	getPendingPlatform,
	getPendingToolUrl,
	goToPendingOrFallback,
	persistPendingToolUrl,
} from "./continueAfterAuth";

const normalizePlatform = (platform) => {
	if (platform === "biz" || platform === "business") return "biz";
	if (platform === "psl" || platform === "personal") return "psl";
	return getPendingPlatform() || "psl";
};

/**
 * Best-effort: whether attempting web+pofsis:// from this page is reasonable.
 * Not a guarantee the OS will open the installed PWA.
 *
 * Primary target: desktop Chrome / Edge / Chromium-like.
 * Returns false on: standalone, iOS/iPadOS, Safari, Firefox, and mobile UAs
 * (manifest protocol_handlers are not treated as generally reliable on mobile).
 *
 * Localhost may return true for desktop Chromium so the click path can be
 * exercised, but final verification should use the HTTPS staging/production
 * origin where the PWA is actually installed.
 */
export const supportsPwaProtocolLaunch = () => {
	if (typeof window === "undefined" || typeof navigator === "undefined") {
		return false;
	}
	if (isStandalone()) return false;

	const ua = navigator.userAgent || "";

	if (/iPhone|iPad|iPod/i.test(ua)) return false;
	if (
		navigator.platform === "MacIntel" &&
		typeof navigator.maxTouchPoints === "number" &&
		navigator.maxTouchPoints > 1
	) {
		return false;
	}

	if (/Firefox\//i.test(ua)) return false;

	const isSafari =
		/Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|EdgiOS|OPR\//i.test(ua);
	if (isSafari) return false;

	// Mobile — protocol handlers not treated as reliable for Open in App here
	if (/Mobi|Android/i.test(ua)) return false;

	if (/Chrome|Chromium|Edg|OPR\//i.test(ua)) return true;

	return false;
};

export const getProtocolContinueUrl = (platform) => {
	const p = normalizePlatform(platform);
	return `${PWA_PROTOCOL_SCHEME}://continue/${p}`;
};

export const getProtocolOpenUrl = (platform) => {
	const p = normalizePlatform(platform);
	return `${PWA_PROTOCOL_SCHEME}://open/${p}`;
};

/** HTTPS continue path (browser fallback / in-tab continuation). Not a PWA launch. */
export const getAppContinueUrl = () => {
	return `${window.location.origin}${continuePath()}`;
};

/** Persist pending callback + continueOnce for the installed app to consume. */
export const preparePendingForApp = (platform) => {
	const p = normalizePlatform(platform);
	const pending = getPendingToolUrl();
	if (pending) {
		persistPendingToolUrl(pending, p);
	} else {
		armContinueOnce();
	}
	return getProtocolContinueUrl(p);
};

/**
 * Attempt to deep-link into the installed POFSIS PWA via web+pofsis://.
 * Does NOT use window.open(httpsUrl). Does NOT auto-fallback to HTTPS.
 *
 * @returns {{ attempted: boolean, mode: string }}
 */
export const tryLaunchInstalledPwa = (platform) => {
	const p = normalizePlatform(platform);
	preparePendingForApp(p);

	if (isStandalone()) {
		goToPendingOrFallback(p);
		return { attempted: true, mode: "standalone-continue" };
	}

	if (!supportsPwaProtocolLaunch()) {
		return { attempted: false, mode: "unsupported" };
	}

	// Same-context custom-protocol navigation from a user gesture.
	// Success cannot be confirmed from JS — keep HTTPS fallback as an
	// explicit secondary click (no timeout / visibility auto-redirect).
	window.location.href = getProtocolContinueUrl(p);
	return { attempted: true, mode: "protocol" };
};

/** Alias for tryLaunchInstalledPwa (protocol attempt, not HTTPS window.open). */
export const openInInstalledApp = tryLaunchInstalledPwa;
